import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { SiteMapEquipmentType } from '@interfaces/ISiteMap';
import { EQUIPMENT_CATALOG } from './siteMap.constants';
import styles from './EquipmentCatalog.module.css';

export interface IEquipmentCatalogProps {
  open: boolean;
  onPick: (type: SiteMapEquipmentType) => void;
  onClose: () => void;
}

/**
 * Anchored popover listing the fixed equipment catalog (spec §5/§8). Not a
 * `ui/Menu` — the contract here is a controlled `open` boolean with no
 * trigger/anchor element to hand the library, so this follows the same
 * "plain positioned card, own dismissal wiring" shape as `PlanPicker` and
 * `StatusesMenu`'s inner dropdown. The parent (SiteMap's toolbar, wired in a
 * later task) is expected to render this inside a `position: relative`
 * wrapper around its "Place equipment" button so the card's own
 * `position: absolute` anchors correctly.
 */
export function EquipmentCatalog({ open, onPick, onClose }: IEquipmentCatalogProps) {
  const { t } = useTranslation();
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current?.contains(e.target as Node)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className={styles.card} ref={rootRef} role="menu" aria-label={t('logistics.siteMap.equipment.catalogTitle')}>
      <p className={styles.title}>{t('logistics.siteMap.equipment.catalogTitle')}</p>
      <ul className={styles.list}>
        {EQUIPMENT_CATALOG.map(({ type, icon: Icon }) => (
          <li key={type}>
            <button
              type="button"
              role="menuitem"
              className={styles.row}
              onClick={() => {
                onPick(type);
                onClose();
              }}
            >
              <Icon size={16} />
              <span className={styles.name}>{t(`logistics.siteMap.equipment.${type}`)}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
